import React, { useState, useEffect } from 'react'

import { useRouter } from 'next/router'
import PageContent from '@components/layout/PageContent'
import useAuthenticationApi from '@hooks/useAuthenticationApi'
import {
  getXCLEARCorrelationInfo,
  setAccessToken,
  setSessionId,
} from '@api/utils'
import { Form, StepSubTitle, StepTitle } from '@components/layout/Form'
import { Enrollment as EnrollmentClient } from '@clear-denver/tpc-openapi-typescript'
import {
  Box,
  Checkbox,
  Flex,
  InputGroup,
  Link,
  Text,
  Button,
} from '@clear/design-system'
import { DiscreteEvent, dispatchCustomEvent, KPI } from '@utils/customEvents'
import VerificationCodeForm from '@components/VerificationCodeForm'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { authSchema } from '@validators/schema/enroll/authSchema'
import LookUpForm from '@components/pages/shared/LookUpForm'
import { Stepper } from '@components/storybook'
import { enrollSteps } from '@utils/steps'
import { useEnrollmentContext } from '@components/EnrollmentContext'

interface FormData extends Partial<EnrollmentClient.BiographicsLookup> {
  otpSessionId: string
}

type InitiateAuth = {
  phoneNumber?: string
  email?: string
  otpSessionId: string
}

const AuthPage: React.FC = () => {
  const router = useRouter()
  const { client: authenticationClient } = useAuthenticationApi()
  const { setBiographicAuthInfo, biographicAuthInfo } = useEnrollmentContext()

  const redirectToDesiredPath = () => {
    const allowedPaths = [
      '/status',
      '/ktn-lookup',
      '/enroll/document-check',
      '/renewal/eligibility/eligibility',
    ]

    const desiredPath = router.query?.desiredPath as string
    const enrollmentId = router.query?.enrollmentId || null
    const territoryId = router.query?.territoryId || null

    if (desiredPath === '/cancel') {
      router.push(
        {
          pathname: '/cancel',
          query: {
            enrollmentId,
          },
        },
        '/cancel'
      )
      return
    }

    if (desiredPath === '/modify') {
      router.push(
        {
          pathname: '/modify',
          query: {
            enrollmentId,
            territoryId,
          },
        },
        '/modify'
      )
      return
    }

    if (desiredPath && allowedPaths.includes(desiredPath)) {
      router.push(desiredPath)
    } else {
      router.push('/')
    }
  }

  const [verificationFormData, setVerificationFormData] =
    useState<FormData | null>(null)
  const { register, handleSubmit, formState } = useForm<any>({
    resolver: yupResolver(authSchema),
    mode: 'onChange',
  })

  enum FormSteps {
    CONTACT = 'Contact',
    VERIFICATION = 'Verification',
    BIOGRAPHICS = 'Biographics',
    RENEWAL = 'Renewal',
  }

  const [step, setStep] = useState<FormSteps>(FormSteps.CONTACT)
  const { errors, isValid } = formState
  const [otpFailureMessage, setOtpFailureMessage] = useState<string>('')
  const [isAuthed, setIsAuthed] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  async function onSubmitContact(formData: InitiateAuth) {
    setIsLoading(true)
    const { phoneNumber, email } = formData
    const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
    const baseMetaData = {
      kpi: KPI.USER_INTERACTION,
      xCLEARCorrelationInfo,
      url: router.route,
    }
    const formatedPhoneNumber = phoneNumber ? `+1.${phoneNumber}` : null
    dispatchCustomEvent(EnrollmentClient.LogEntryLevelEnum.INFO, {
      payload: {
        message: 'Initiate auth begin',
      },
      metadata: {
        ...baseMetaData,
        event: DiscreteEvent.INITIATE_AUTH_BEGIN,
      },
    })
    try {
      const response = await authenticationClient.initiateAuth({
        xCLEARCorrelationInfo,
        otpForm: {
          phoneNumber: formatedPhoneNumber,
          emailAddress: email,
        },
      })
      setBiographicAuthInfo({
        ...biographicAuthInfo,
        phoneNumber: formatedPhoneNumber,
        emailAddress: email,
      })
      if (response?.otpSessionId) {
        setVerificationFormData({
          ...formData,
          otpSessionId: response.otpSessionId,
        })
        setStep(FormSteps.VERIFICATION)
      }
      dispatchCustomEvent(EnrollmentClient.LogEntryLevelEnum.INFO, {
        payload: {
          message: 'Initiate auth successful',
        },
        metadata: {
          ...baseMetaData,
          event: DiscreteEvent.INITIATE_AUTH_SUCCESS,
        },
      })
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      dispatchCustomEvent(EnrollmentClient.LogEntryLevelEnum.ERROR, {
        payload: {
          message: error?.message,
          stack: error?.stack,
        },
        metadata: {
          ...baseMetaData,
          event: DiscreteEvent.INITIATE_AUTH_FAILURE,
        },
      })
    }
  }

  async function onVerificationSubmit(formData) {
    setIsLoading(true)
    const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
    const baseMetaData = {
      kpi: KPI.USER_INTERACTION,
      xCLEARCorrelationInfo,
      url: router.route,
    }
    dispatchCustomEvent(EnrollmentClient.LogEntryLevelEnum.INFO, {
      payload: {
        message: 'OTP verification begin',
      },
      metadata: {
        ...baseMetaData,
        event: DiscreteEvent.OTP_VERIFICATION_BEGIN,
      },
    })
    try {
      await authenticationClient.otpChallenge({
        xCLEARCorrelationInfo,
        otpCode: {
          otpSessionId: verificationFormData.otpSessionId,
          otpCode: formData.verificationCode,
        },
      })
      if (router.query?.desiredPath === '/renewal/eligibility/eligibility') {
        router.push(
          {
            pathname: '/renewal/eligibility/eligibility',
            query: {
              otpSessionId: verificationFormData?.otpSessionId,
            },
          },
          '/renewal/eligibility/eligibility'
        )
      } else {
        setStep(FormSteps.BIOGRAPHICS)
      }

      dispatchCustomEvent(EnrollmentClient.LogEntryLevelEnum.INFO, {
        payload: {
          message: 'OTP verification successful',
        },
        metadata: {
          ...baseMetaData,
          event: DiscreteEvent.OTP_VERIFICATION_SUCCESS,
        },
      })
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      setOtpFailureMessage(
        'Invalid verification code provided, please try again.'
      )
      dispatchCustomEvent(EnrollmentClient.LogEntryLevelEnum.ERROR, {
        payload: {
          message: error?.message,
          stack: error?.stack,
        },
        metadata: {
          ...baseMetaData,
          event: DiscreteEvent.OTP_VERIFICATION_FAILURE,
        },
      })
    }
  }

  useEffect(() => {
    if (
      isAuthed &&
      router.query?.desiredPath !== '/renewal/eligibility/eligibility'
    ) {
      redirectToDesiredPath()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed])

  async function onSubmitByBiographic(
    formData: EnrollmentClient.BiographicsLookup
  ) {
    const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
    const baseMetaData = {
      kpi: KPI.USER_INTERACTION,
      xCLEARCorrelationInfo,
      url: router.route,
    }
    const { countryOfBirth, dob, firstName, lastName, middleName } = formData
    dispatchCustomEvent(EnrollmentClient.LogEntryLevelEnum.INFO, {
      payload: {
        message: 'Biographics submit begin',
      },
      metadata: {
        ...baseMetaData,
        event: DiscreteEvent.AUTH_SUBMIT_BIOGRAPHICS_BEGIN,
      },
    })
    try {
      const { id, token } = await authenticationClient.signIn({
        xCLEARCorrelationInfo,
        signInRequest: {
          otpSessionId: verificationFormData.otpSessionId,
          biographics: {
            countryOfBirth,
            dob,
            firstName,
            lastName,
            middleName,
            noMiddleName: !middleName,
          },
        },
      })
      setAccessToken(token)
      setSessionId(id)
      dispatchCustomEvent(EnrollmentClient.LogEntryLevelEnum.INFO, {
        payload: {
          message: 'Biographics submit successful',
        },
        metadata: {
          ...baseMetaData,
          event: DiscreteEvent.AUTH_SUBMIT_BIOGRAPHICS_SUCCESS,
        },
      })
      setBiographicAuthInfo({ ...biographicAuthInfo, ...formData })
      setIsAuthed(true)
    } catch (error) {
      dispatchCustomEvent(EnrollmentClient.LogEntryLevelEnum.ERROR, {
        payload: {
          message: error?.message,
          stack: error?.stack,
        },
        metadata: {
          ...baseMetaData,
          event: DiscreteEvent.AUTH_SUBMIT_BIOGRAPHICS_FAILURE,
        },
      })
    }
  }

  return (
    <PageContent>
      {step === FormSteps.BIOGRAPHICS && (
        <>
          <Stepper current={0} percent={0} steps={enrollSteps} />
          <StepTitle>Personal Information</StepTitle>
          <LookUpForm
            headTitle="Personal Information | TSA&nbsp;PreCheck®"
            showSuffix
            onSubmitByBiographic={onSubmitByBiographic}
          />
        </>
      )}
      {step === FormSteps.VERIFICATION && (
        <Box
          sx={{
            width: '350px',
            marginLeft: 'auto',
            marginRight: 'auto',
            border: '1px solid #999',
            borderRadius: '8px',
            padding: '24px 20px',
          }}
        >
          <VerificationCodeForm
            formData={verificationFormData}
            onSubmit={onVerificationSubmit}
            otpFailureMessage={otpFailureMessage}
            isLoading={isLoading}
            onSubmitContact={onSubmitContact}
            verificationFormData={verificationFormData}
          />
        </Box>
      )}
      {step === FormSteps.CONTACT && (
        <Box
          sx={{
            width: '350px',
            marginLeft: 'auto',
            marginRight: 'auto',
            border: '1px solid #999',
            borderRadius: '8px',
            padding: '24px 20px',
          }}
        >
          <Box
            className="pb-4 px-4"
            as="img"
            minWidth={0}
            src="/tsa-pre-logo.svg"
            alt="TSA pre-check with CLEAR logo"
          />

          <StepSubTitle>
            Sign in or create an account to submit a TSA&nbsp;PreCheck®
            application, check your status, or look up KTN
          </StepSubTitle>

          <StepSubTitle color="brandDarkBlue" fontWeight="bold" fontSize="14px">
            Please Use Your Email and Phone Number
          </StepSubTitle>

          <Form onSubmit={handleSubmit(onSubmitContact)}>
            <InputGroup
              data-cy="email"
              placeholder={'Email'}
              {...register('email')}
              error={errors.email?.message}
              containerProps={{ padding: 0 }}
            />

            <Flex flexDirection="row">
              <InputGroup
                data-cy={'countryCode'}
                name="countryCode"
                disabled
                value="+1"
                containerProps={{
                  padding: 0,
                  width: '20%',
                }}
              />

              <InputGroup
                data-cy="phoneNumber"
                placeholder={'Phone number'}
                {...register('phoneNumber')}
                error={errors.phoneNumber?.message}
                containerProps={{
                  padding: 0,
                  width: '90%',
                }}
              />
            </Flex>

            <Checkbox
              text={
                <Text
                  as="p"
                  color="neutralsTextGray"
                  variant="body"
                  fontSize="12px"
                >
                  By providing my telephone number and checking this box, I
                  agree to the{' '}
                  <Link
                    target="blank"
                    variant="regular"
                    color="brandDarkBlue"
                    href="/terms"
                  >
                    Terms & Conditions
                  </Link>{' '}
                  and I consent to receive multifactor text messages related to
                  my TSA&nbsp;PreCheck® account.
                </Text>
              }
              data-cy="acceptTerms"
              {...register('acceptTerms')}
              containerProps={{ padding: 0, marginY: 20 }}
            />

            <Button
              type="submit"
              text="Send Login Code"
              borderRadius="0rem"
              disabled={
                Boolean(Object.keys(errors).length) || !isValid || isLoading
              }
              height="3rem !important"
            />
          </Form>
        </Box>
      )}
    </PageContent>
  )
}

export default AuthPage
